import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COLORMAP_KEYS, colormapGradientCSS } from './colormaps';

const MENU_MARGIN = 12;
const MENU_GAP = 6;
const MENU_PADDING = 5;
const MENU_SWATCH_WIDTH = 56;
const MENU_WIDTH = MENU_SWATCH_WIDTH + MENU_PADDING * 2;
const MENU_Z_INDEX = 1000;
const MOBILE_BREAKPOINT = 640;

function detectBottomSheet() {
  if (typeof window === 'undefined') return false;
  const coarse = window.matchMedia?.('(pointer: coarse)').matches;
  return coarse || window.innerWidth <= MOBILE_BREAKPOINT;
}

function getMenuLayout(rect, swatchHeight) {
  const naturalHeight =
    MENU_PADDING * 2 +
    COLORMAP_KEYS.length * swatchHeight +
    Math.max(0, COLORMAP_KEYS.length - 1) * 3 +
    2;

  const spaceBelow = Math.max(96, window.innerHeight - rect.bottom - MENU_MARGIN - MENU_GAP);
  const spaceAbove = Math.max(96, rect.top - MENU_MARGIN - MENU_GAP);
  const openUp = naturalHeight > spaceBelow && spaceAbove > spaceBelow;
  const availableHeight = openUp ? spaceAbove : spaceBelow;
  const height = Math.min(naturalHeight, availableHeight);
  const unclampedLeft = rect.left + rect.width / 2 - MENU_WIDTH / 2;
  const left = Math.max(MENU_MARGIN, Math.min(unclampedLeft, window.innerWidth - MENU_MARGIN - MENU_WIDTH));
  const top = openUp
    ? Math.max(MENU_MARGIN, rect.top - MENU_GAP - height)
    : Math.min(rect.bottom + MENU_GAP, window.innerHeight - MENU_MARGIN - height);

  return { left, top, maxHeight: availableHeight, openUp };
}

export default function ColormapPicker({ value, onChange, theme }) {
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState({ left: MENU_MARGIN, top: MENU_MARGIN, maxHeight: 240 });
  const [useBottomSheet, setUseBottomSheet] = useState(detectBottomSheet);
  const triggerRef = useRef(null);
  const sheetRef = useRef(null);
  const swatchHeight = useBottomSheet ? 24 : 11;
  const triggerHeight = useBottomSheet ? 18 : 14;
  const sheetMaxHeight = typeof window !== 'undefined'
    ? Math.min(window.innerHeight * 0.6, window.innerHeight - 120)
    : 360;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updatePresentation = () => setUseBottomSheet(detectBottomSheet());
    updatePresentation();

    window.addEventListener('resize', updatePresentation);
    return () => window.removeEventListener('resize', updatePresentation);
  }, []);

  useLayoutEffect(() => {
    if (!open || useBottomSheet || !triggerRef.current || typeof window === 'undefined') return;

    const updateLayout = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setLayout(getMenuLayout(rect, swatchHeight));
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, true);
    return () => {
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout, true);
    };
  }, [open, swatchHeight, useBottomSheet]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open || !useBottomSheet || typeof document === 'undefined') return undefined;

    const preventBackgroundScroll = (event) => {
      if (sheetRef.current?.contains(event.target)) return;
      event.preventDefault();
    };

    document.addEventListener('wheel', preventBackgroundScroll, { passive: false });
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventBackgroundScroll);
      document.removeEventListener('touchmove', preventBackgroundScroll);
    };
  }, [open, useBottomSheet]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        title="Change colormap"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Colormap: ${value}. Click to change.`}
        style={{
          display: 'block',
          width: 56,
          height: triggerHeight,
          borderRadius: 3,
          cursor: 'pointer',
          background: colormapGradientCSS(value),
          outline: open ? '1px solid rgba(255,255,255,0.45)' : '1px solid rgba(138,143,152,0.3)',
          outlineOffset: '0px',
          border: 'none',
          padding: 0,
        }}
      />
      {open && createPortal(
        <div data-theme={theme}>
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: MENU_Z_INDEX,
              background: useBottomSheet ? 'rgba(0,0,0,0.18)' : 'transparent',
              touchAction: useBottomSheet ? 'none' : 'auto',
            }}
          />
          <div
            ref={sheetRef}
            role="listbox"
            style={{
              position: 'fixed',
              left: useBottomSheet ? MENU_MARGIN : layout.left,
              right: useBottomSheet ? MENU_MARGIN : 'auto',
              top: useBottomSheet ? 'auto' : layout.top,
              bottom: useBottomSheet ? `max(${MENU_MARGIN}px, calc(env(safe-area-inset-bottom) + ${MENU_MARGIN}px))` : 'auto',
              width: useBottomSheet ? 'auto' : MENU_WIDTH,
              maxHeight: useBottomSheet ? sheetMaxHeight : layout.maxHeight,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              overscrollBehaviorY: 'contain',
              touchAction: useBottomSheet ? 'pan-y' : 'auto',
              background: 'var(--c-card)',
              border: '1px solid var(--c-border-2)',
              borderRadius: useBottomSheet ? 14 : 6,
              padding: useBottomSheet
                ? `10px 10px calc(10px + env(safe-area-inset-bottom))`
                : `${MENU_PADDING}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              zIndex: MENU_Z_INDEX + 1,
              boxShadow: useBottomSheet
                ? '0 14px 36px rgba(0,0,0,0.45)'
                : '0 6px 18px rgba(0,0,0,0.4)',
            }}
          >
            {useBottomSheet && (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 4 }}>
                  <div style={{ width: 36, height: 4, borderRadius: 999, background: 'rgba(138,143,152,0.45)' }} />
                </div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--c-text-3)',
                  padding: '0 2px 4px',
                }}>
                  Colormap
                </div>
              </>
            )}
            {COLORMAP_KEYS.map((key) => (
              <button
                key={key}
                role="option"
                aria-selected={key === value}
                onClick={() => {
                  onChange(key);
                  setOpen(false);
                }}
                title={key}
                style={{
                  display: 'block',
                  width: useBottomSheet ? '100%' : MENU_SWATCH_WIDTH,
                  height: swatchHeight,
                  minHeight: swatchHeight,
                  borderRadius: useBottomSheet ? 6 : 3,
                  cursor: 'pointer',
                  background: colormapGradientCSS(key),
                  outline: key === value ? '1.5px solid rgba(255,255,255,0.55)' : '1px solid rgba(138,143,152,0.2)',
                  outlineOffset: '0px',
                  border: 'none',
                  padding: 0,
                  flexShrink: 0,
                }}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
